import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Download, FileText, Lock, Sparkles, Flame, ShoppingBag, Loader2 } from "lucide-react";

function BRL(v) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}

function AccessBadge({ type }) {
    if (type === "free") {
        return <span className="px-2.5 py-1 rounded-full bg-[#00D4D4]/15 text-[#0a7a7a] text-[10px] tracking-widest font-display uppercase">Grátis</span>;
    }
    if (type === "premium") {
        return <span className="px-2.5 py-1 rounded-full bg-[#9370DB]/15 text-[#5a3fa0] text-[10px] tracking-widest font-display uppercase">Premium</span>;
    }
    return <span className="px-2.5 py-1 rounded-full bg-[#D4AF37]/15 text-[#8a7022] text-[10px] tracking-widest font-display uppercase">Avulso</span>;
}

function FeaturedTag({ tag }) {
    if (tag === "new") {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1A1A1A] text-white text-[10px] tracking-widest font-display uppercase">
                <Sparkles className="w-3 h-3" /> Lançamento
            </span>
        );
    }
    if (tag === "trending") {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FF6B6B]/15 text-[#c4453d] text-[10px] tracking-widest font-display uppercase">
                <Flame className="w-3 h-3" /> Em alta
            </span>
        );
    }
    return null;
}

function EbookCard({ ebook, billing, onBuy, buying }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const locked = !ebook.can_download;
    const reason = ebook.lock_reason;
    const hasActiveSub = billing?.has_access;
    const showSubscribeCta = locked && (reason === "subscription_required" || reason === "login_required");
    const showBuyCta = locked && ebook.purchasable && !hasActiveSub;

    const download = async () => {
        try {
            const { data } = await api.get(`/library/ebooks/${ebook.id}/download`);
            window.open(data.url, "_blank", "noopener");
        } catch (e) { alert(formatApiError(e)); }
    };

    return (
        <article
            data-testid={`ebook-${ebook.id}`}
            className={`group relative bg-white border rounded-2xl overflow-hidden transition-all ${
                locked ? "border-black/10" : "border-black/5 hover:border-[#00D4D4]/40 hover:-translate-y-1"
            }`}
        >
            {/* Cover */}
            <div className="relative aspect-[4/3] overflow-hidden bg-[#FAFAF7]">
                {ebook.cover && (
                    <img
                        src={resolveUrl(ebook.cover)}
                        alt={ebook.title}
                        className={`w-full h-full object-cover transition-transform duration-500 ${locked ? "scale-100 brightness-95" : "group-hover:scale-105"}`}
                        loading="lazy"
                    />
                )}
                {locked && (
                    <div className="absolute inset-0 bg-black/35 flex items-center justify-center backdrop-blur-[1px]">
                        <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                            <Lock className="w-5 h-5 text-[#1A1A1A]" />
                        </div>
                    </div>
                )}
                {/* Top-left badges */}
                <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    <AccessBadge type={ebook.access_type} />
                    <FeaturedTag tag={ebook.featured_tag} />
                </div>
            </div>

            {/* Body */}
            <div className="p-5">
                <div className="overline text-[#5C5C5C]">{ebook.category}</div>
                <h3 className="font-display text-lg mt-1.5 tracking-tight line-clamp-2">{ebook.title}</h3>
                <p className="font-body text-sm text-[#5C5C5C] mt-2 line-clamp-2">{ebook.description}</p>

                {/* CTAs */}
                <div className="mt-5 space-y-2">
                    {!locked && (
                        <button
                            data-testid={`ebook-download-${ebook.id}`}
                            onClick={download}
                            className="w-full justify-center btn-primary"
                        >
                            <Download className="w-4 h-4" /> Baixar PDF
                        </button>
                    )}
                    {showSubscribeCta && (
                        <button
                            data-testid={`ebook-subscribe-${ebook.id}`}
                            onClick={() => navigate(user ? "/planos" : "/login", { state: { from: "/biblioteca" } })}
                            className="w-full justify-center btn-primary"
                        >
                            <Sparkles className="w-4 h-4" /> {user ? "Assinar plano" : "Entrar para assinar"}
                        </button>
                    )}
                    {showBuyCta && (
                        <button
                            data-testid={`ebook-buy-${ebook.id}`}
                            onClick={() => onBuy(ebook.id)}
                            disabled={buying === ebook.id}
                            className={`w-full justify-center ${showSubscribeCta ? "btn-ghost" : "btn-primary"}`}
                        >
                            {buying === ebook.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                            {buying === ebook.id ? "Redirecionando..." : `Comprar por ${BRL(ebook.price_brl)}`}
                        </button>
                    )}
                    {locked && !showSubscribeCta && !showBuyCta && (
                        <div className="text-sm font-body text-[#5C5C5C] flex items-center gap-2"><FileText className="w-4 h-4" /> Conteúdo restrito</div>
                    )}
                </div>
            </div>
        </article>
    );
}

export default function Library() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [ebooks, setEbooks] = useState([]);
    const [billing, setBilling] = useState(null);
    const [filter, setFilter] = useState("all"); // all | free | premium | paid | mine
    const [buying, setBuying] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/library/ebooks").then((r) => setEbooks(r.data)).catch(() => {});
        if (user) api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
    }, [user]);

    const buy = async (ebookId) => {
        setError("");
        if (!user) {
            navigate("/login", { state: { from: "/biblioteca" } });
            return;
        }
        setBuying(ebookId);
        try {
            const { data } = await api.post("/billing/checkout/ebook", {
                ebook_id: ebookId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            setError(formatApiError(e));
            setBuying(null);
        }
    };

    const visible = ebooks.filter((e) => {
        if (filter === "all") return true;
        if (filter === "mine") return e.can_download && e.access_type !== "free";
        return e.access_type === filter;
    });

    const filters = [
        { id: "all", label: "Tudo" },
        { id: "free", label: "Grátis" },
        { id: "premium", label: "Premium" },
        { id: "paid", label: "Avulso" },
        ...(user ? [{ id: "mine", label: "Meus" }] : []),
    ];

    return (
        <div className="min-h-screen relative grain">
            <Header />
            <main className="max-w-[1400px] mx-auto px-6 md:px-10 pt-14 pb-24">
                <div className="overline text-[#5C5C5C]">Biblioteca digital</div>
                <h1 className="font-display font-light text-5xl md:text-6xl mt-2 tracking-tight">
                    E-books, <span className="font-editorial italic text-holo">protocolos</span> & guias
                </h1>
                <p className="font-body text-[#5C5C5C] mt-4 max-w-2xl">
                    Materiais complementares cuidadosamente elaborados pelos mentores. Itens premium estão liberados para assinantes ativos.
                </p>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2 mt-10">
                    {filters.map((f) => (
                        <button
                            key={f.id}
                            data-testid={`library-filter-${f.id}`}
                            onClick={() => setFilter(f.id)}
                            className={`px-4 py-2 rounded-full text-sm font-display transition-colors ${
                                filter === f.id ? "bg-[#1A1A1A] text-white" : "bg-white border border-black/5 text-[#1A1A1A] hover:border-black/20"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                    {billing?.has_access && (
                        <span className="ml-auto inline-flex items-center gap-2 text-xs font-body text-[#00D4D4]">
                            <Sparkles className="w-3.5 h-3.5" /> Acesso premium ativo
                        </span>
                    )}
                </div>

                {error && (
                    <div data-testid="library-error" className="mt-5 text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{error}</div>
                )}

                <div data-testid="library-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                    {visible.map((eb) => (
                        <EbookCard key={eb.id} ebook={eb} billing={billing} onBuy={buy} buying={buying} />
                    ))}
                    {visible.length === 0 && (
                        <div className="col-span-full text-center py-16 font-body text-[#5C5C5C]">Nenhum item neste filtro.</div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
